'use client';

/**
 * StrapSelector (Gallet)
 *
 * Inline strap selector for the watches PDP. Any strap can go on any head, so we
 * render the head's bundle variations (resolved from Saleor) grouped by strap
 * material into selectable tabs, with a colour carousel per material. Nothing
 * is selected until the user picks a material (click again to deselect and hide
 * colours). Picking a colour navigates to that combination's SKU via `onSelect`.
 *
 * Distinct from the Breitling `ChangeStrapButton` (a modal trigger). Strap data
 * comes from the orchestrator's `bundleVariations` (Saleor, not Algolia).
 */

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CropImage } from '@/components/ui/CropImage';
import { Icon } from '@/components/ui';
import { useStableImageLoadHandlers } from '@/hooks/useStableImageLoadHandlers';
import { cdn } from '@/lib/cdn';
import type { BundleVariation } from '@/lib/product/types';

import styles from './StrapSelector.module.scss';

const VISIBLE_DESKTOP = 3;
const VISIBLE_MOBILE = 2;
// Keep in sync with the xs-up breakpoint ($breakpoint-xs: 600px) and the
// --strap-selector-visible-colors variable in StrapSelector.module.scss.
const STRAP_DESKTOP_MQ = '(min-width: 600px)';

function useVisibleCount(): number {
  const [count, setCount] = useState(VISIBLE_MOBILE);
  useEffect(() => {
    const mql = window.matchMedia(STRAP_DESKTOP_MQ);
    const update = () => setCount(mql.matches ? VISIBLE_DESKTOP : VISIBLE_MOBILE);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return count;
}

interface MaterialBucket {
  key: string;
  label: string;
  variations: BundleVariation[];
  /** Representative variation used for the tab's cropped swatch. */
  swatch: BundleVariation;
}

interface StrapSelectorProps {
  currentSku: string;
  variations: BundleVariation[];
  /** Navigate to the chosen combination SKU. */
  onSelect: (sku: string) => void;
  className?: string;
}

/** Known buckets with i18n labels under `product.strapSelector.materials.*`. */
export const KNOWN_MATERIAL_BUCKETS = [
  'Metal',
  'Leather',
  'Fabric',
  'Rubber',
  'Alcantara',
  'Other',
] as const;

export type MaterialBucketKey = (typeof KNOWN_MATERIAL_BUCKETS)[number];

/**
 * Map a variation's Saleor material data onto one of the display buckets.
 *
 * The haystack combines the category (`wbr-category-materials`) and marketing
 * material (`wbr-material-mktg`). Saleor stores some values as PIM codes — e.g.
 * `STNSTL`, `STSTL`, `STNSTL_18KREG` for stainless steel — which surface raw
 * when a localized label is missing, so we match codes as well as clean names
 * (vocabulary mirrors the legacy `getStrapMaterial`).
 *
 * Do not match bare `bracelet` — in FR/DE/ES locales the category is often
 * "Bracelet cuir veau" / "Lederarmband" (strap/bracelet + material), not metal.
 *
 * Order matters: Rubber is checked before Leather so combination straps like
 * "Calfskin leather & rubber" bucket as Rubber (matching legacy), and Alcantara
 * before Fabric (it is a fabric-like).
 */
export function materialBucketKey(variation: BundleVariation): string {
  const haystack = `${variation.materialCategory ?? ''} ${variation.material ?? ''}`.toLowerCase();
  if (
    /metal|métal|metall|metal bracelet|bracelet en métal|bracelet métal|steel|stnstl|ststl|stainless|edelstahl|acciaio|acero|inox|inossidab|inoxidable|titan|gold|18k|platin|dlc/.test(
      haystack
    )
  )
    return 'Metal';
  if (/alcantara/.test(haystack)) return 'Alcantara';
  if (/rubber|caoutchouc|kautschuk|gummi|caucho|goma|caucci|silicone/.test(haystack))
    return 'Rubber';
  if (
    /leather|leathr|calf|alligator|allig|croco|nabuk|nubuck|suede|cuir|cuero|piel|pelle|leder|kalbsleder|becerro|vitello/.test(
      haystack
    )
  )
    return 'Leather';
  if (
    /fabric|textile|textil|tejido|tessuto|stoff|polyamide|poliamid|econyl|yarn|nylon|canvas|nato|tissu/.test(
      haystack
    )
  )
    return 'Fabric';
  // Fall back to the raw category/material so nothing is silently dropped.
  return variation.materialCategory || variation.material || 'Other';
}

/** Stable display order for the known buckets; unknowns sort to the end. */
const BUCKET_ORDER: MaterialBucketKey[] = ['Metal', 'Leather', 'Fabric', 'Rubber', 'Alcantara'];

function isKnownMaterialBucket(key: string): key is MaterialBucketKey {
  return (KNOWN_MATERIAL_BUCKETS as readonly string[]).includes(key);
}

function sortVariationsWithCurrentFirst(
  variations: BundleVariation[],
  currentSku: string
): BundleVariation[] {
  const currentUpper = currentSku.toUpperCase();
  return [...variations].sort((a, b) => {
    const aCurrent = a.sku.toUpperCase() === currentUpper;
    const bCurrent = b.sku.toUpperCase() === currentUpper;
    if (aCurrent && !bCurrent) return -1;
    if (!aCurrent && bCurrent) return 1;
    return 0;
  });
}

function groupByMaterial(variations: BundleVariation[], currentSku: string): MaterialBucket[] {
  const currentUpper = currentSku.toUpperCase();
  const map = new Map<string, BundleVariation[]>();
  for (const v of variations) {
    const key = materialBucketKey(v);
    const list = map.get(key);
    if (list) list.push(v);
    else map.set(key, [v]);
  }
  return Array.from(map.entries())
    .map(([key, list]) => {
      const sortedVariations = sortVariationsWithCurrentFirst(list, currentSku);
      return { key, label: key, variations: sortedVariations, swatch: sortedVariations[0] };
    })
    .sort((a, b) => {
      const aHasCurrent = a.variations.some((v) => v.sku.toUpperCase() === currentUpper);
      const bHasCurrent = b.variations.some((v) => v.sku.toUpperCase() === currentUpper);
      if (aHasCurrent && !bHasCurrent) return -1;
      if (!aHasCurrent && bHasCurrent) return 1;

      const ai = isKnownMaterialBucket(a.key) ? BUCKET_ORDER.indexOf(a.key) : -1;
      const bi = isKnownMaterialBucket(b.key) ? BUCKET_ORDER.indexOf(b.key) : -1;
      return (ai === -1 ? BUCKET_ORDER.length : ai) - (bi === -1 ? BUCKET_ORDER.length : bi);
    });
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12 8"
      width="12"
      height="8"
      aria-hidden
      focusable="false"
    >
      <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function StrapSelector({
  currentSku,
  variations,
  onSelect,
  className,
}: Readonly<StrapSelectorProps>) {
  const t = useTranslations('product.strapSelector');
  const currentUpper = currentSku.toUpperCase();

  const buckets = useMemo(() => groupByMaterial(variations, currentSku), [variations, currentSku]);

  const materialLabel = (key: string) => (isKnownMaterialBucket(key) ? t(`materials.${key}`) : key);

  // Nothing selected until the user picks a material; colour carousel stays hidden.
  // If `selectedKey` no longer matches a bucket (variation set changed), `active`
  // is simply undefined — no need to sync state in an effect.
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [loadedImageUrls, setLoadedImageUrls] = useState<Set<string>>(() => new Set());

  const markImageLoaded = useCallback((url: string) => {
    setLoadedImageUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  if (buckets.length === 0) return null;

  const active = selectedKey ? buckets.find((b) => b.key === selectedKey) : undefined;

  const toggleMaterial = (key: string) => {
    setSelectedKey((prev) => (prev === key ? undefined : key));
  };

  return (
    <section className={clsx(styles.root, className)} data-testid="StrapSelector">
      <h2 className={styles.heading}>{t('heading')}</h2>

      <ul className={styles.materials}>
        {buckets.map((bucket) => {
          const isSelected = bucket.key === selectedKey;
          return (
            <li key={bucket.key}>
              <button
                type="button"
                className={clsx(styles.material, isSelected && styles.materialSelected)}
                aria-pressed={isSelected}
                aria-label={t(isSelected ? 'hideMaterial' : 'selectMaterial', {
                  material: materialLabel(bucket.key),
                })}
                onClick={() => toggleMaterial(bucket.key)}
              >
                <span className={styles.swatch}>
                  <CropImage
                    src={bucket.swatch.image || ''}
                    alt=""
                    coords={bucket.swatch.strapPreviewCoords}
                    width={40}
                    height={40}
                  />
                </span>
                <span className={styles.materialLabel}>{materialLabel(bucket.key)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {active ? (
        <ColorCarousel
          material={active}
          currentSku={currentUpper}
          loadedImageUrls={loadedImageUrls}
          onImageLoaded={markImageLoaded}
          onSelect={onSelect}
          colorLabel={t('color')}
          prevLabel={t('previousColors')}
          nextLabel={t('nextColors')}
          selectLabel={(name) => t('selectStrap', { name })}
        />
      ) : null}
    </section>
  );
}

interface ColorCarouselProps {
  material: MaterialBucket;
  currentSku: string;
  loadedImageUrls: Set<string>;
  onImageLoaded: (url: string) => void;
  onSelect: (sku: string) => void;
  colorLabel: string;
  prevLabel: string;
  nextLabel: string;
  selectLabel: (name: string) => string;
}

function ColorCarousel({
  material,
  currentSku,
  loadedImageUrls,
  onImageLoaded,
  onSelect,
  colorLabel,
  prevLabel,
  nextLabel,
  selectLabel,
}: ColorCarouselProps) {
  const items = material.variations;
  const [start, setStart] = useState(0);
  const visible = useVisibleCount();
  const imageLoadHandlers = useStableImageLoadHandlers(onImageLoaded);

  // Reset scroll position when the selected material changes.
  useEffect(() => {
    setStart(0);
  }, [material.key]);

  const maxStart = Math.max(0, items.length - visible);
  const clampedStart = Math.min(start, maxStart);
  const isCarousel = items.length > visible;
  const isCentered = items.length < visible;
  const canPrev = clampedStart > 0;
  const canNext = clampedStart < maxStart;

  const navigate = (sku: string) => {
    if (sku.toUpperCase() !== currentSku) onSelect(sku);
  };

  return (
    <div className={styles.colorBlock}>
      <span className={styles.colorLabel}>{colorLabel}</span>

      <div className={styles.carousel}>
        {/* Arrow rails are always reserved so the card size stays identical
            across materials; the buttons are hidden when there's nothing to
            scroll (fewer colours than the viewport holds). */}
        <button
          type="button"
          className={clsx(styles.arrow, !isCarousel && styles.arrowHidden)}
          onClick={() => setStart((s) => Math.max(0, s - 1))}
          disabled={!canPrev}
          aria-hidden={!isCarousel || undefined}
          tabIndex={isCarousel ? undefined : -1}
          aria-label={prevLabel}
        >
          <ChevronIcon className={styles.arrowPrev} />
        </button>

        <div className={styles.viewport}>
          <ul
            className={clsx(styles.track, isCentered && styles.trackCentered)}
            style={{ transform: `translateX(-${clampedStart * (100 / visible)}%)` }}
          >
            {items.map((v) => {
              const isCurrent = v.sku.toUpperCase() === currentSku;
              const name = v.color || v.name;
              const imageUrl = v.image
                ? cdn.transformImageUrl(v.image, {
                    width: 400,
                    quality: 85,
                    format: 'auto',
                  })
                : '';
              const isLoaded = !imageUrl || loadedImageUrls.has(imageUrl);

              return (
                <li key={v.sku} className={styles.colorItem}>
                  <button
                    type="button"
                    className={clsx(styles.colorButton, isCurrent && styles.colorButtonSelected)}
                    aria-current={isCurrent}
                    aria-label={selectLabel(name)}
                    onClick={() => navigate(v.sku)}
                  >
                    <span className={styles.colorImageWrap}>
                      {imageUrl ? (
                        <>
                          {!isLoaded ? (
                            <span className={styles.colorImageLoading} aria-hidden="true">
                              <Icon name="spinner" size={20} className={styles.colorImageSpinner} />
                            </span>
                          ) : null}
                          <img
                            src={imageUrl}
                            alt={name}
                            className={clsx(styles.colorImage, isLoaded && styles.colorImageLoaded)}
                            loading="lazy"
                            data-image-url={imageUrl}
                            ref={imageLoadHandlers.ref}
                            onLoad={imageLoadHandlers.onLoad}
                            onError={imageLoadHandlers.onError}
                          />
                        </>
                      ) : null}
                    </span>
                    <span className={styles.colorName}>{name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <button
          type="button"
          className={clsx(styles.arrow, !isCarousel && styles.arrowHidden)}
          onClick={() => setStart((s) => Math.min(maxStart, s + 1))}
          disabled={!canNext}
          aria-hidden={!isCarousel || undefined}
          tabIndex={isCarousel ? undefined : -1}
          aria-label={nextLabel}
        >
          <ChevronIcon className={styles.arrowNext} />
        </button>
      </div>

      {/* Decorative scroll-position dots (one per position, tracking the
          arrows), carousel mode only. The row itself is always reserved so
          materials without a carousel don't shift the CTA below. */}
      <ul className={styles.dots} aria-hidden>
        {isCarousel &&
          Array.from({ length: maxStart + 1 }, (_, position) => (
            <li key={position}>
              <span className={clsx(styles.dot, position === clampedStart && styles.dotActive)} />
            </li>
          ))}
      </ul>
    </div>
  );
}

export default StrapSelector;
